import React, { useMemo, useState } from 'react';
import {
    AlertTriangle, CheckCircle2, FolderUp, Info, Link2, Loader2, Plus, RefreshCw,
} from 'lucide-react';
import { holdIdleExit } from '../../../services/idleHold';
import * as asplanService from '../../../services/asplanService';
import type { SchreibKontext } from '../../../services/asplanService';
import type { ProjektSteckbriefStand } from '../../../services/projektSteckbrief';
import { useDokumente } from '../../dokumente/useDokumente';
import type { ProjektDokument } from '../../dokumente/types';
import {
    aushangBlaetterOhneLuecken, verfuegbareAushaenge, type AushangEintrag,
} from '../aushaenge/aushangKatalog';
import { verfuegbareFormulare } from '../formulare/formularKatalog';
import type { ProjektBestand } from '../useProjektBestand';
import type { AsplanBundle, SchleusenParameter, Unterlage } from '../types';
import {
    abgleicheAblage, anlageAusDokument, anlagenZeilen, offeneVormerkungen,
    offenerNachweisHinweis, verknuepfung, vormerkungEntwurf,
    type AblageDokument, type Druckstueck,
} from './anlagen';
import { baueAsplanPdf, legeAsplanPdfAb } from './asplanAblage';
import { entwurfAusSteckbriefAnlage, steckbriefAnlagen, type SteckbriefAnlage } from './steckbriefAnlagen';

/**
 * „Druckstücke und Nachweise“ — die Karte, an der Plan, Projektablage und
 * Anlagenliste zusammenkommen.
 *
 * ═══ WAS HIER ENTSCHIEDEN WIRD: NICHTS ════════════════════════════════════
 * Jede Aussage dieser Karte — welche Unterlage belegt ist, welches Druckstück
 * noch fehlt, welches Dokument sich verknüpfen lässt — kommt aus `anlagen.ts`
 * und ist dort ohne DOM geprüft. Diese Datei ordnet an und ruft auf.
 *
 * ═══ AUSHÄNGE UND FORMULARE WERDEN VORGEMERKT, NICHT ERZEUGT ══════════════
 * Sie entstehen im Browserdruck (A3/A4, große Schrift, Piktogramme) und
 * damit gar nicht erst als Datei. Sie hier ein zweites Mal mit jsPDF zu
 * zeichnen wäre derselbe Fehler, den die Skizzen-Doktrin des Moduls
 * ausschließt: zwei Zeichenwege driften auseinander, und der Unterschied
 * fällt erst am Bauzaun auf. Der ehrliche Weg ist die Vormerkung — der
 * Eintrag steht in der Anlagenliste als „wird nachgereicht“, und wer das
 * ausgefüllte Blatt eingescannt hat, lädt es in die Projektablage und
 * verknüpft es hier.
 *
 * ═══ AUS DER PLANUNGSPHASE: VORLIEGENDE UNTERLAGEN MIT BELEG ══════════════
 * Die Unterlagen-Checkliste der Planungsphase kennt zu jedem Gutachten den
 * Status und den Beleg in der Projektablage. Was dort `liegt_vor` ist und
 * fuer den A+S-Plan gekennzeichnet, erscheint hier als Vorschlag fuer
 * Kapitel 9 — mit demselben Dokument als Nachweis. Welche Zeile neu entsteht,
 * welche vorhandene nur belegt wird und wo kein Knopf steht, entscheidet
 * `steckbriefAnlagen.ts` auf Grundlage des Abgleichs. Uebernommen wird eine
 * Zeile je Klick, nie beim Laden (Hausregel aller Folgeunterlagen).
 */

interface Props {
    projectId: string;
    bundle: AsplanBundle;
    /**
     * Der Projektsteckbrief — im Container EINMAL gelesen, wie `bestand`.
     * Vor `bereit` ist nichts bekannt; das ist etwas anderes als „nichts
     * liegt vor", und die Karte sagt es so.
     */
    steckbrief: ProjektSteckbriefStand;
    /**
     * Stammdaten und Notrufplan des Projekts — im Container EINMAL gelesen.
     *
     * Diese Karte legt das Plan-PDF ab; der Notrufplan traegt darin die
     * Rettungskette. Ohne ihn druckte das ABGELEGTE Blatt „nicht
     * ausgewiesen", waehrend das heruntergeladene die Klinik nennt — und
     * abgelegt ist das Blatt, auf das sich spaeter jemand beruft.
     */
    bestand: ProjektBestand;
    projektName?: string;
    projektNummer?: string;
    projektAdresse?: string;
    schleuse?: SchleusenParameter;
    company?: unknown;
    quoteConfig?: unknown;
    invoiceConfig?: unknown;
    /** Freigegeben oder Nur-Ansicht: an der Anlagenliste wird nichts geändert. */
    gesperrt: boolean;
    /** Nur-Ansicht: es wird auch nichts abgelegt. */
    nurAnsicht: boolean;
    benutzerId?: string | null;
    benutzerName?: string | null;
    ctx: SchreibKontext;
    schreiben: <T>(vorgang: () => Promise<T>) => Promise<T | null>;
}

const KNOPF = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold '
    + 'border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 disabled:opacity-40';

/** Dieselben Worte wie die Auswahl „Art" in der Unterlagenliste. */
const ART_LABEL: Record<Unterlage['art'], string> = {
    grundlage: 'verwendete Unterlage',
    anlage: 'Anlage des Plans',
};

export const AblageKarte: React.FC<Props> = ({
    projectId, bundle, steckbrief, bestand, projektName, projektNummer, projektAdresse, schleuse,
    company, quoteConfig, invoiceConfig, gesperrt, nurAnsicht,
    benutzerId, benutzerName, ctx, schreiben,
}) => {
    const { bereit, notrufplan } = bestand;
    // 'temp-id' ist die Vorbelegung eines nie gespeicherten Projekts. Ohne
    // Zeile in der Datenbank gibt es keine Ablage — derselbe Riegel wie im
    // DokumentePanel.
    const gespeichert = Boolean(projectId) && projectId !== 'temp-id';
    const { dokumente, laden, fehler: ladeFehler, neuLaden, uebernehmen } =
        useDokumente(gespeichert ? projectId : null);

    const [arbeitet, setArbeitet] = useState<string | null>(null);
    const [fehler, setFehler] = useState<string | null>(null);
    const [meldung, setMeldung] = useState<string | null>(null);
    /**
     * Anmerkungen des Ablage-Kerns zur letzten Ablage (etwa: der Steckbrief-
     * Stand fuer den Aenderungsdienst wurde nicht festgehalten). Kein Fehler
     * — die Fassung liegt —, aber auch kein uneingeschraenkter Erfolg:
     * verschluckt wuerde aus einer fehlenden Ueberwachung ein gruener Satz.
     */
    const [hinweise, setHinweise] = useState<string[]>([]);

    /** Nur die Dokumente, die dieses Modul angeht. */
    const eigene = useMemo<ProjektDokument[]>(
        () => dokumente.filter(d => (d.module || []).includes('asplan')),
        [dokumente],
    );

    const ablage = useMemo<AblageDokument[]>(
        () => eigene.map(d => ({
            id: d.id, titel: d.titel, dateiname: d.dateiname, nur_admin: d.nur_admin,
        })),
        [eigene],
    );

    const abgleich = useMemo(
        () => abgleicheAblage(bundle.unterlagen, ablage),
        [bundle.unterlagen, ablage],
    );

    /**
     * ALLE Dokumente des Projekts, nicht nur die des Moduls: der Beleg eines
     * Schadstoffgutachtens liegt in der Ablage des Bauherrn (Gutachten,
     * Nachweise), nicht unter `module: asplan`. Die Vorschlaege aus der
     * Planungsphase schauen deshalb auf die ganze Ablage.
     */
    const alleDokumente = useMemo<AblageDokument[]>(
        () => dokumente.map(d => ({
            id: d.id, titel: d.titel, dateiname: d.dateiname, nur_admin: d.nur_admin,
        })),
        [dokumente],
    );

    const sb = steckbrief.steckbrief;
    const ausPlanung = useMemo<SteckbriefAnlage[]>(
        () => (sb ? steckbriefAnlagen(sb.unterlagen, bundle.unterlagen, alleDokumente) : []),
        [sb, bundle.unterlagen, alleDokumente],
    );

    const zeilen = useMemo(() => [
        ...anlagenZeilen(bundle.unterlagen, 'grundlage'),
        ...anlagenZeilen(bundle.unterlagen, 'anlage'),
    ], [bundle.unterlagen]);

    const hinweis = offenerNachweisHinweis(zeilen);

    /**
     * Die Aushänge — mit ihrer Lücken-Aussage, sobald der Notrufplan gelesen ist.
     *
     * Die Lücke eines Aushangs hängt am Notrufplan des SiGe-Plans. Vor der
     * Antwort darf sie gar nicht erst entstehen: `verfuegbareAushaenge(bundle,
     * null)` behauptete „im Notrufplan ist keine Notfallstelle hinterlegt“,
     * wo in Wahrheit noch niemand nachgesehen hat. Solange `bereit` falsch
     * ist, nimmt die Karte deshalb die Blätter OHNE Lücken — dieselbe
     * Unterscheidung, die auch über das Ablegen entscheidet.
     */
    const aushaenge = useMemo<AushangEintrag[]>(
        () => (bereit
            ? verfuegbareAushaenge(bundle, notrufplan)
            : aushangBlaetterOhneLuecken(bundle)),
        [bereit, bundle, notrufplan],
    );

    const formulare = useMemo(() => verfuegbareFormulare(bundle), [bundle]);

    /** Aushänge und Formulare als vormerkbare Druckstücke. */
    const angebot = useMemo<Druckstueck[]>(() => [
        ...aushaenge.map(a => ({
            gattung: 'aushang' as const, key: a.key, titel: a.titel,
        })),
        ...formulare.map(f => ({
            gattung: 'formular' as const, key: f.key, titel: f.titel,
        })),
    ], [aushaenge, formulare]);

    /**
     * Was den Blättern noch fehlt — ausdrücklich UNABHÄNGIG davon, ob sie
     * schon vorgemerkt sind.
     *
     * Eine Lücke hört nicht auf, offen zu sein, weil jemand das Blatt in die
     * Anlagenliste aufgenommen hat (Regel 9: der Plan zeigt auch das Offene).
     * Hinge die Anzeige an den offenen Vormerkungen, verschwände sie genau
     * dann, wenn das Blatt als Anlage des Plans geführt wird.
     */
    const luecken = useMemo(
        () => [
            ...aushaenge.map(x => ({ id: `aushang:${x.key}`, titel: x.titel, fehlt: x.fehlt })),
            ...formulare.map(x => ({ id: `formular:${x.key}`, titel: x.titel, fehlt: x.fehlt })),
        ].filter((x): x is { id: string; titel: string; fehlt: string } => Boolean(x.fehlt)),
        [aushaenge, formulare],
    );

    const offen = useMemo(
        () => offeneVormerkungen(angebot, bundle.unterlagen),
        [angebot, bundle.unterlagen],
    );

    const zugehoerig = () => ({
        project_id: bundle.plan.project_id,
        asplan_id: bundle.plan.id,
    });

    const melde = (text: string) => { setFehler(null); setMeldung(text); };

    // ── Vorgaenge ──────────────────────────────────────────────────────────

    const planAblegen = async () => {
        setFehler(null);
        setMeldung(null);
        setHinweise([]);
        if (!gespeichert) {
            setFehler('Ohne gespeichertes Projekt gibt es keine Projektablage.');
            return;
        }
        // Der Riegel bis `bereit` — siehe die Begruendung am Knopf. Er steht
        // hier ZUSAETZLICH zum `disabled`: zwischen dem Rendern des Knopfes
        // und dem Klick kann sich der Ladezustand geaendert haben, und ein
        // abgelegtes PDF laesst sich nicht zurueckholen.
        if (!bereit) {
            setFehler('Der Notrufplan des SiGe-Plans wird noch gelesen. '
                + 'Einen Augenblick — sonst friert diese Fassung eine '
                + 'Rettungskette ein, die niemand nachgesehen hat.');
            return;
        }
        setArbeitet('plan');
        const freigeben = holdIdleExit('A+S-Plan wird abgelegt');
        try {
            const erzeugt = baueAsplanPdf({
                bundle, company, quoteConfig, invoiceConfig,
                optionen: {
                    projektName, projektNummer,
                    bauvorhabenAdresse: projektAdresse,
                    schleuse,
                    // Dieselbe Eingabe wie beim Download (`AsplanModul.exportiere`).
                    // Fehlte sie hier, druckte ausgerechnet das ABGELEGTE Blatt
                    // „die Rettungskette ist in diesem Ausdruck nicht
                    // ausgewiesen", waehrend der heruntergeladene Plan die
                    // Klinik nennt — und die Ablage ist der Nachweis.
                    //
                    // `bereit` ist oben schon geprueft; `notrufplan` heisst an
                    // dieser Stelle also wirklich „gelesen und keiner
                    // hinterlegt" und nicht „noch nicht nachgesehen".
                    notrufplan,
                },
            });
            if (erzeugt.status === 'fehler') { setFehler(erzeugt.fehler); return; }

            const abgelegt = await legeAsplanPdfAb({
                projectId, doc: erzeugt.wert, projektName,
                plan: bundle.plan, benutzerId, benutzerName,
                // Stammdaten fuer den Planungsstand, den die Ablage bei einer
                // freigegebenen Fassung mit festhaelt (Aenderungsdienst).
                metadata: bestand.metadata,
            });
            if (abgelegt.status === 'fehler') { setFehler(abgelegt.fehler); return; }

            uebernehmen(abgelegt.wert);
            melde(`Abgelegt als „${abgelegt.wert.titel}“. Frühere Fassungen bleiben erhalten.`);
            // Die Hinweise des Kerns zusaetzlich zur Erfolgsmeldung — nie verschluckt.
            setHinweise(abgelegt.hinweise || []);
        } finally {
            freigeben();
            setArbeitet(null);
        }
    };

    const vormerken = async (d: Druckstueck) => {
        setFehler(null);
        setMeldung(null);
        setArbeitet(`vormerkung:${d.gattung}:${d.key}`);
        try {
            await schreiben(() => asplanService.createUnterlage(ctx, {
                ...zugehoerig(),
                ...vormerkungEntwurf(d, bundle.unterlagen.length),
            }));
            melde(`„${d.titel}“ steht jetzt als Anlage im Plan — bis zum Hochladen als „wird nachgereicht“.`);
        } finally {
            setArbeitet(null);
        }
    };

    const verknuepfen = async (u: Pick<Unterlage, 'id' | 'bezeichnung'>, dokumentId: string) => {
        setFehler(null);
        setMeldung(null);
        setArbeitet(`verknuepfen:${u.id}`);
        try {
            await schreiben(() => asplanService.updateUnterlage(ctx, u.id, verknuepfung(dokumentId)));
            melde(`„${u.bezeichnung}“ ist jetzt durch ein Dokument der Projektablage belegt.`);
        } finally {
            setArbeitet(null);
        }
    };

    const alsAnlage = async (d: AblageDokument) => {
        setFehler(null);
        setMeldung(null);
        const entwurf = anlageAusDokument(d, bundle.unterlagen.length);
        if (!entwurf) {
            setFehler('Ein admin-gesperrtes Dokument wird nicht als Anlage geführt: '
                + 'für alle anderen ist es nicht vorhanden, und der Plan darf nichts '
                + 'behaupten, was sein Leser nicht einsehen kann.');
            return;
        }
        setArbeitet(`anlage:${d.id}`);
        try {
            await schreiben(() => asplanService.createUnterlage(ctx, {
                ...zugehoerig(), ...entwurf,
            }));
            melde(`„${entwurf.bezeichnung}“ steht jetzt als Anlage im Plan.`);
        } finally {
            setArbeitet(null);
        }
    };

    /**
     * Nimmt eine vorliegende Unterlage der Planungsphase MIT ihrem Beleg in
     * Kapitel 9 auf. Der Entwurf entsteht gegen den Bestand dieses
     * Augenblicks: ist der Beleg inzwischen verknuepft, wird nichts angelegt
     * und der Grund steht da.
     */
    const ausPlanungAufnehmen = async (v: SteckbriefAnlage) => {
        setFehler(null);
        setMeldung(null);
        const entwurf = entwurfAusSteckbriefAnlage(v, bundle.unterlagen);
        if (!entwurf) {
            setFehler(`„${v.name}“ ist inzwischen durch dieses Dokument belegt oder nicht übernehmbar — `
                + 'die Liste wurde neu gelesen.');
            return;
        }
        setArbeitet(`planung:${v.id}`);
        try {
            await schreiben(() => asplanService.createUnterlage(ctx, {
                ...zugehoerig(), ...entwurf,
            }));
            melde(`„${v.name}“ steht jetzt in Kapitel 9 als „liegt vor“ — belegt durch `
                + `„${v.dokumentTitel || 'Dokument'}“ aus der Projektablage.`);
        } finally {
            setArbeitet(null);
        }
    };

    const widerspruchLoesen = async (id: string) => {
        setFehler(null);
        setMeldung(null);
        setArbeitet(`widerspruch:${id}`);
        try {
            await schreiben(() => asplanService.updateUnterlage(ctx, id, { nachgereicht: false }));
        } finally {
            setArbeitet(null);
        }
    };

    // ── Anzeige ────────────────────────────────────────────────────────────

    /** Die Unterlagen, denen noch ein Nachweis fehlt — Ziele zum Verknüpfen. */
    const unbelegte = bundle.unterlagen.filter(u => !u.dokument_id);

    return (
        <section className="bg-white border border-slate-200 rounded-2xl p-4 mb-4">
            <div className="flex items-start justify-between gap-3 mb-0.5">
                <h2 className="font-bold text-slate-800">Druckstücke und Nachweise</h2>
                <button type="button" className={KNOPF} onClick={() => void neuLaden()} disabled={laden}>
                    {laden ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                    Ablage neu lesen
                </button>
            </div>
            <p className="text-xs text-slate-500 mb-3">
                Kapitel 9 des Plans weist aus, was tatsächlich vorliegt: „liegt vor“ heißt,
                dass das Dokument in der Projektablage liegt. Alles andere steht rot auf dem Blatt.
            </p>

            {fehler && (
                <p className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {fehler}
                </p>
            )}
            {ladeFehler && !fehler && (
                <p className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    Die Projektablage ließ sich nicht lesen: {ladeFehler}
                </p>
            )}
            {meldung && (
                <p className="mb-3 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
                    {meldung}
                </p>
            )}
            {hinweise.length > 0 && (
                <ul className="mb-3 rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 space-y-1">
                    {hinweise.map((h, i) => (
                        <li key={`${i}:${h}`} className="flex items-start gap-1.5">
                            <Info className="w-3.5 h-3.5 shrink-0 mt-0.5 text-slate-400" />
                            <span className="min-w-0 break-words">{h}</span>
                        </li>
                    ))}
                </ul>
            )}

            {/* ─── Plan ablegen ─────────────────────────────────────────── */}
            <div className="rounded-xl border border-slate-200 p-3 mb-3">
                <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <p className="text-sm font-bold text-slate-700">Diese Fassung ablegen</p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                            {bundle.plan.rev_label
                                ? `Revision ${bundle.plan.rev_label}`
                                : 'Entwurf ohne Revisionskennung'}
                            {' — '}
                            eine neue Fassung tritt neben die alte, sie ersetzt sie nicht.
                        </p>
                    </div>
                    {/* Gesperrt, solange der Notrufplan nicht gelesen ist.
                        Der Download darf in diesem Moment laufen — er gibt
                        eine Datei aus, die man wegwirft und neu zieht. Eine
                        abgelegte Fassung ist der Nachweis: sie bleibt stehen,
                        laesst sich nicht ueberschreiben, und ein halb
                        geladener Stand waere darin eingefroren. */}
                    <button
                        type="button"
                        className={KNOPF}
                        disabled={nurAnsicht || !bereit || arbeitet === 'plan'}
                        title={bereit
                            ? undefined
                            : 'Der Notrufplan des SiGe-Plans wird noch gelesen.'}
                        onClick={() => void planAblegen()}
                    >
                        {arbeitet === 'plan' || !bereit
                            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            : <FolderUp className="w-3.5 h-3.5" />}
                        In die Projektablage legen
                    </button>
                </div>
                {!bereit && (
                    <p className="text-[11px] text-slate-500 mt-1.5">
                        Der Notrufplan des SiGe-Plans wird gelesen — er trägt die Rettungskette
                        im abgelegten Plan. Bis dahin bleibt das Ablegen gesperrt: eine abgelegte
                        Fassung wird nicht überschrieben, ein halb gelesener Stand bliebe als
                        Nachweis stehen.
                    </p>
                )}
                {eigene.length > 0 && (
                    <ul className="mt-2 space-y-1">
                        {eigene.map(d => (
                            <li key={d.id} className="text-[11px] text-slate-500 flex items-center gap-1.5">
                                <CheckCircle2 className="w-3 h-3 text-emerald-500 shrink-0" />
                                <span className="truncate">{d.titel}</span>
                                {d.nur_admin && <span className="text-amber-700 shrink-0">· admin-gesperrt</span>}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* ─── Nachweisstand ────────────────────────────────────────── */}
            {hinweis && (
                <p className="mb-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                    {hinweis}
                </p>
            )}

            {abgleich.widersprueche.length > 0 && (
                <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                    <p className="text-xs font-bold text-amber-800 mb-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                        Als „wird nachgereicht“ gekennzeichnet, obwohl das Dokument vorliegt
                    </p>
                    <ul className="space-y-1">
                        {abgleich.widersprueche.map(z => (
                            <li key={z.id} className="flex items-center justify-between gap-2 text-xs text-amber-900">
                                <span className="truncate">{z.bezeichnung}</span>
                                <button
                                    type="button"
                                    className={KNOPF}
                                    disabled={gesperrt || arbeitet === `widerspruch:${z.id}`}
                                    onClick={() => void widerspruchLoesen(z.id)}
                                >
                                    Kennzeichen zurücknehmen
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {abgleich.verwaist.length > 0 && (
                <div className="mb-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2">
                    <p className="text-xs font-bold text-amber-800 mb-1">
                        <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                        Verweis ohne einsehbares Dokument
                    </p>
                    <p className="text-[11px] text-amber-900 mb-1.5">
                        Das verknüpfte Dokument ist gelöscht oder admin-gesperrt. Der Plan
                        druckt weiterhin „liegt vor“ — bitte neu verknüpfen oder den Verweis lösen.
                    </p>
                    <ul className="text-xs text-amber-900 space-y-0.5">
                        {abgleich.verwaist.map(z => <li key={z.id}>{z.bezeichnung}</li>)}
                    </ul>
                </div>
            )}

            {/* ─── Verknüpfen ───────────────────────────────────────────── */}
            {abgleich.unverknuepft.length > 0 && (
                <div className="rounded-xl border border-slate-200 p-3 mb-3">
                    <p className="text-sm font-bold text-slate-700 mb-1.5">
                        In der Ablage, aber nicht im Plan
                    </p>
                    <ul className="space-y-2">
                        {abgleich.unverknuepft.map(d => (
                            <li key={d.id} className="rounded-lg bg-slate-50 px-2.5 py-2">
                                <p className="text-xs font-semibold text-slate-700 truncate">{d.titel}</p>
                                <div className="flex flex-wrap items-center gap-1.5 mt-1.5">
                                    <button
                                        type="button"
                                        className={KNOPF}
                                        disabled={gesperrt || arbeitet === `anlage:${d.id}`}
                                        onClick={() => void alsAnlage(d)}
                                    >
                                        <Plus className="w-3.5 h-3.5" /> als Anlage aufnehmen
                                    </button>
                                    {unbelegte.map(u => (
                                        <button
                                            key={u.id}
                                            type="button"
                                            className={KNOPF}
                                            disabled={gesperrt || arbeitet === `verknuepfen:${u.id}`}
                                            onClick={() => void verknuepfen(u, d.id)}
                                        >
                                            <Link2 className="w-3.5 h-3.5" />
                                            belegt „{u.bezeichnung || 'ohne Bezeichnung'}“
                                        </button>
                                    ))}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* ─── Aus der Planungsphase ────────────────────────────────── */}
            <div className="rounded-xl border border-slate-200 p-3 mb-3">
                <p className="text-sm font-bold text-slate-700">Aus der Planungsphase: Unterlagen, die vorliegen</p>
                <p className="text-[11px] text-slate-500 mt-0.5 mb-2">
                    Die Unterlagen-Checkliste der Planungsphase führt, was mit Beleg in der Projektablage
                    vorliegt. Übernommen steht die Unterlage in Kapitel 9 als „liegt vor“ — belegt durch
                    dasselbe Dokument. Übernommen wird eine Zeile je Klick.
                </p>
                {!steckbrief.bereit ? (
                    <p className="text-xs text-slate-400 py-1 inline-flex items-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Die Planungsphase wird gelesen …
                    </p>
                ) : sb?.lesefehler.planungsphase ? (
                    <p className="rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800">
                        <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                        Die Planungsphase konnte nicht gelesen werden: {sb.lesefehler.planungsphase}.
                        Was dort vorliegt, ist damit nicht bekannt — hier fehlt nichts, hier ist nichts gelesen.
                    </p>
                ) : sb && !sb.planungsphase.vorhanden ? (
                    <p className="text-xs text-slate-400 py-1">
                        Noch keine Planungsphase angelegt — es gibt keine Unterlagen-Checkliste, aus der
                        Kapitel 9 lesen könnte.
                    </p>
                ) : laden ? (
                    <p className="text-xs text-slate-400 py-1 inline-flex items-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Die Projektablage wird gelesen …
                    </p>
                ) : ausPlanung.length === 0 ? (
                    <p className="text-xs text-slate-400 py-1">
                        Die Planungsphase führt keine A+S-relevante Unterlage mit Status „liegt vor“.
                    </p>
                ) : (
                    <ul className="space-y-1.5">
                        {ausPlanung.map(v => {
                            // Als eigene Bindungen, damit die Eingrenzung in den
                            // Klick-Funktionen erhalten bleibt.
                            const ziel = v.ziel;
                            const dokumentId = v.dokumentId;
                            return (
                            <li key={v.id} className="rounded-lg bg-slate-50 px-2.5 py-2">
                                <div className="flex items-start justify-between gap-2">
                                    <div className="min-w-0">
                                        <p className="text-xs font-semibold text-slate-700">{v.name}</p>
                                        <p className="text-[11px] text-slate-500 truncate">
                                            {ART_LABEL[v.art]}
                                            {v.dokumentTitel && ` · Beleg: ${v.dokumentTitel}`}
                                        </p>
                                    </div>
                                    {v.stand === 'neu' && (
                                        <button
                                            type="button"
                                            className={`${KNOPF} shrink-0`}
                                            disabled={gesperrt || arbeitet !== null}
                                            onClick={() => void ausPlanungAufnehmen(v)}
                                        >
                                            {arbeitet === `planung:${v.id}`
                                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                : <Plus className="w-3.5 h-3.5" />}
                                            mit Beleg aufnehmen
                                        </button>
                                    )}
                                    {v.stand === 'verknuepfen' && ziel && dokumentId && (
                                        <button
                                            type="button"
                                            className={`${KNOPF} shrink-0`}
                                            disabled={gesperrt || arbeitet !== null}
                                            onClick={() => void verknuepfen(ziel, dokumentId)}
                                        >
                                            {arbeitet === `verknuepfen:${ziel.id}`
                                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                                : <Link2 className="w-3.5 h-3.5" />}
                                            belegt „{ziel.bezeichnung || 'ohne Bezeichnung'}“
                                        </button>
                                    )}
                                    {v.stand === 'uebernommen' && (
                                        <span className="text-[11px] text-emerald-700 inline-flex items-center gap-1 shrink-0">
                                            <CheckCircle2 className="w-3.5 h-3.5" /> steht in Kapitel 9
                                        </span>
                                    )}
                                </div>
                                {v.stand === 'nicht_einsehbar' && (
                                    <p className="text-[11px] text-amber-800 mt-1">
                                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                                        Der Beleg der Planungsphase ist in der Ablage nicht einsehbar (gelöscht oder
                                        admin-gesperrt) — ohne prüfbaren Nachweis wird nichts übernommen.
                                    </p>
                                )}
                                {v.stand === 'ohne_beleg' && (
                                    <p className="text-[11px] text-amber-800 mt-1">
                                        <AlertTriangle className="w-3 h-3 inline mr-1" />
                                        In der Planungsphase als „liegt vor“ geführt, aber ohne Beleg in der
                                        Projektablage — für Kapitel 9 kein Nachweis. Dort das Dokument verknüpfen
                                        oder hier über den Katalogvorschlag aufnehmen (dann rot, bis belegt).
                                    </p>
                                )}
                            </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            {/* ─── Vormerken ────────────────────────────────────────────── */}
            <div className="rounded-xl border border-slate-200 p-3">
                <p className="text-sm font-bold text-slate-700">Aushänge und Formulare vormerken</p>
                <p className="text-[11px] text-slate-500 mt-0.5 mb-2">
                    Diese Blätter entstehen über den Browserdruck und nicht als Datei. Vorgemerkt
                    stehen sie in Kapitel 9 als „wird nachgereicht“; das eingescannte Exemplar
                    laden Sie in die Projektablage und verknüpfen es oben.
                </p>
                {offen.length === 0 ? (
                    <p className="text-xs text-slate-400 py-1">
                        Alle Aushänge und Formulare stehen bereits in der Anlagenliste.
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-1.5">
                        {offen.map(d => (
                            <button
                                key={`${d.gattung}:${d.key}`}
                                type="button"
                                className={KNOPF}
                                disabled={gesperrt || arbeitet === `vormerkung:${d.gattung}:${d.key}`}
                                onClick={() => void vormerken(d)}
                            >
                                <Plus className="w-3.5 h-3.5" /> {d.titel}
                            </button>
                        ))}
                    </div>
                )}

                {/* Was den Blättern fehlt. Ein vorgemerktes Blatt ist deshalb
                    nicht vollständig — die Lücke bleibt hier stehen, bis sie
                    an ihrer Quelle geschlossen ist. */}
                {!bereit ? (
                    <p className="text-[11px] text-slate-400 mt-2">
                        Ob diesen Blättern noch etwas fehlt, steht fest, sobald der Notrufplan
                        des SiGe-Plans gelesen ist.
                    </p>
                ) : luecken.length > 0 && (
                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2">
                        <p className="text-xs font-bold text-amber-800 mb-1">
                            <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />
                            Diese Blätter sind noch nicht vollständig
                        </p>
                        <ul className="space-y-1">
                            {luecken.map(l => (
                                <li key={l.id} className="text-[11px] text-amber-900">
                                    <b>{l.titel}</b> — {l.fehlt}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>
        </section>
    );
};
